// Pages/UpdateShippingAddress.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Styling/InvoiceCard.css";
import axios from "axios";
import {
  fetchCustomerByCode,
  fetchAllCountries,
  fetchRegionsByCountry,
  fetchDistrictsByRegion,
  fetchTehsilsByDistrict,
  fetchCitiesByTehsil,
  saveShippingAddress,
  fetchCustomerCode
} from "../Api/InvoiceApi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Base_URL } from "../url";

const UpdateShippingAddress = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [billingAddress, setBillingAddress] = useState({
    name: "",
    address1: "",
    address2: "",
    address3: "",
    countryCode: "",
    regionName: "",
    districtName: "",
    tehsilName: "",
    cityName: "",
    pinCode: ""
  });

  const [shippingAddress, setShippingAddress] = useState({
    shippingAddressId: "",
    name: "",
    address1: "",
    address2: "",
    address3: "",
    countryCode: "",
    regionCode: "",
    districtCode: "",
    tehsilCode: "",
    cityCode: "",
    pinCode: ""
  });

  const [dropdownData, setDropdownData] = useState({
    countries: [],
    regions: [],
    districts: [],
    tehsils: [],
    cities: []
  });

  const [loading, setLoading] = useState({
    customer: false,
    countries: false,
    regions: false,
    districts: false,
    tehsils: false,
    cities: false,
    saving: false,
    shippingLoad: false
  });

  const copyingShippingRef = useRef(false);
  const [check, setCheck] = useState(false);
  const [customerCode, setCustomerCode] = useState("");

  // Fetch initial data: customer code, billing address, countries, and the saved shipping address
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(prev => ({ ...prev, shippingLoad: true }));
        const savedCustomerCode = await fetchCustomerCode(invoiceId);
        if (!savedCustomerCode) {
          toast.error("Customer code not found for this invoice.");
          return;
        }
        setCustomerCode(savedCustomerCode);
        await fetchBillingAddress(savedCustomerCode);
        await fetchCountries();
        await fetchAndPopulateShippingAddress(invoiceId);
      } catch (err) {
        console.error("Initialize error:", err);
        toast.error("Failed to initialize update shipping address screen.");
      } finally {
        setLoading(prev => ({ ...prev, shippingLoad: false }));
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const fetchBillingAddress = async (code) => {
    setLoading(prev => ({ ...prev, customer: true }));
    try {
      const customerData = await fetchCustomerByCode(code);
      setBillingAddress({
        name: customerData.name || "",
        address1: customerData.address1 || "",
        address2: customerData.address2 || "",
        address3: customerData.address3 || "",
        countryCode: customerData.countryCode || "",
        regionName: customerData.regionName || "",
        districtName: customerData.districtName || "",
        tehsilName: customerData.tehsilName || "",
        cityName: customerData.cityName || "",
        pinCode: customerData.pinCode || ""
      });
    } catch (err) {
      console.error("Error fetching billing address:", err);
      toast.error("Failed to load billing address");
    } finally {
      setLoading(prev => ({ ...prev, customer: false }));
    }
  };

  const fetchCountries = async () => {
    setLoading(prev => ({ ...prev, countries: true }));
    try {
      const countries = await fetchAllCountries();
      setDropdownData(prev => ({ ...prev, countries: countries || [] }));
    } catch (err) {
      console.error("Error fetching countries:", err);
      toast.error("Failed to load countries");
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  };

  const fetchRegions = async (countryCode) => {
    if (!countryCode) return [];
    setLoading(prev => ({ ...prev, regions: true }));
    try {
      const regions = await fetchRegionsByCountry(countryCode);
      setDropdownData(prev => ({ ...prev, regions: regions || [] , districts: [], tehsils: [], cities: []}));
      return regions || [];
    } catch (err) {
      console.error("Error fetching regions:", err);
      toast.error("Failed to load regions");
      return [];
    } finally {
      setLoading(prev => ({ ...prev, regions: false }));
    }
  };

  const fetchDistricts = async (regionCode) => {
    if (!regionCode) return [];
    setLoading(prev => ({ ...prev, districts: true }));
    try {
      const districts = await fetchDistrictsByRegion(regionCode);
      setDropdownData(prev => ({ ...prev, districts: districts || [], tehsils: [], cities: [] }));
      return districts || [];
    } catch (err) {
      console.error("Error fetching districts:", err);
      toast.error("Failed to load districts");
      return [];
    } finally {
      setLoading(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchTehsils = async (districtCode) => {
    if (!districtCode) return [];
    setLoading(prev => ({ ...prev, tehsils: true }));
    try {
      const tehsils = await fetchTehsilsByDistrict(districtCode);
      setDropdownData(prev => ({ ...prev, tehsils: tehsils || [], cities: [] }));
      return tehsils || [];
    } catch (err) {
      console.error("Error fetching tehsils:", err);
      toast.error("Failed to load tehsils");
      return [];
    } finally {
      setLoading(prev => ({ ...prev, tehsils: false }));
    }
  };

  const fetchCities = async (tehsilCode) => {
    if (!tehsilCode) return [];
    setLoading(prev => ({ ...prev, cities: true }));
    try {
      const cities = await fetchCitiesByTehsil(tehsilCode);
      setDropdownData(prev => ({ ...prev, cities: cities || [] }));
      return cities || [];
    } catch (err) {
      console.error("Error fetching cities:", err);
      toast.error("Failed to load cities");
      return [];
    } finally {
      setLoading(prev => ({ ...prev, cities: false }));
    }
  };

  // fetch shipping address for the invoice and populate dropdowns/selects
  const fetchAndPopulateShippingAddress = async (invId) => {
    try {
      const res = await fetch(`${Base_URL}/Invoices/GetShippingAddress/id?id=${encodeURIComponent(invId)}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch shipping address (${res.status})`);
      }
      const data = await res.json();
      // Note: API example had pinCode as "" and invoiceId etc.
      setShippingAddress({
        shippingAddressId: data.shippingAddressId || "",
        name: data.name || "",
        address1: data.address1 || "",
        address2: data.address2 || "",
        address3: data.address3 || "",
        countryCode: data.countryCode || "",
        regionCode: data.regionCode || "",
        districtCode: data.districtCode || "",
        tehsilCode: data.tehsilCode || "",
        cityCode: data.cityCode || "",
        pinCode: data.pinCode || ""
      });

      // populate dependent dropdowns in order if codes exist
      if (data.countryCode) {
        const regions = await fetchRegions(data.countryCode);
        if (data.regionCode) {
          // make sure region exists in list
          const regionFound = regions.find(r => r.regionCode === data.regionCode);
          if (regionFound) {
            if (data.regionCode) {
              const districts = await fetchDistricts(data.regionCode);
              if (data.districtCode) {
                const districtFound = districts.find(d => (d.code === data.districtCode));
                if (districtFound) {
                  const tehsils = await fetchTehsils(data.districtCode);
                  if (data.tehsilCode) {
                    const tehsilFound = tehsils.find(t => (t.code === data.tehsilCode));
                    if (tehsilFound) {
                      await fetchCities(data.tehsilCode);
                    }
                  } else {
                    // no tehsil code provided, nothing to fetch for cities
                  }
                }
              }
            }
          } else {
            // region code not found — leave dropdowns as is
          }
        }
      }
    } catch (err) {
      console.error("Error fetching shipping address:", err);
      toast.error("Failed to load shipping address for this invoice");
    }
  };

  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));

    // Fetch dependent dropdowns when country/region/district/tehsil changes
    if (name === "countryCode") {
      // reset dependent codes
      setShippingAddress(prev => ({ ...prev, regionCode: "", districtCode: "", tehsilCode: "", cityCode: "" }));
      fetchRegions(value);
    } else if (name === "regionCode") {
      setShippingAddress(prev => ({ ...prev, districtCode: "", tehsilCode: "", cityCode: "" }));
      fetchDistricts(value);
    } else if (name === "districtCode") {
      setShippingAddress(prev => ({ ...prev, tehsilCode: "", cityCode: "" }));
      fetchTehsils(value);
    } else if (name === "tehsilCode") {
      setShippingAddress(prev => ({ ...prev, cityCode: "" }));
      fetchCities(value);
    }
  };

  const copyBillingToShipping = async () => {
    copyingShippingRef.current = true;
    setCheck(true);

    setShippingAddress(prev => ({
      ...prev,
      name: billingAddress.name || "",
      address1: billingAddress.address1 || "",
      address2: billingAddress.address2 || "",
      address3: billingAddress.address3 || "",
      countryCode: billingAddress.countryCode || "",
      regionCode: "",
      districtCode: "",
      tehsilCode: "",
      cityCode: "",
      pinCode: billingAddress.pinCode || ""
    }));

    try {
      const regions = billingAddress.countryCode ? await fetchRegions(billingAddress.countryCode) : [];
      let matchedRegion = null;
      if (regions && regions.length) {
        matchedRegion = regions.find(r =>
          (r.regionName && r.regionName.toString() === (billingAddress.regionName || "").toString()) ||
          (r.regionCode && r.regionCode.toString() === (billingAddress.regionName || "").toString())
        );
      }
      if (matchedRegion) {
        setShippingAddress(prev => ({ ...prev, regionCode: matchedRegion.regionCode }));
      }

      if (matchedRegion && matchedRegion.regionCode) {
        const districts = await fetchDistricts(matchedRegion.regionCode);
        const matchedDistrict = districts.find(d =>
          (d.description && d.description.toString() === (billingAddress.districtName || "").toString()) ||
          (d.code && d.code.toString() === (billingAddress.districtName || "").toString())
        );
        if (matchedDistrict) {
          setShippingAddress(prev => ({ ...prev, districtCode: matchedDistrict.code }));
        }

        const districtCodeToUse = matchedDistrict ? matchedDistrict.code : "";
        if (districtCodeToUse) {
          const tehsils = await fetchTehsils(districtCodeToUse);
          const matchedTehsil = tehsils.find(t =>
            (t.description && t.description.toString() === (billingAddress.tehsilName || "").toString()) ||
            (t.code && t.code.toString() === (billingAddress.tehsilName || "").toString())
          );
          if (matchedTehsil) {
            setShippingAddress(prev => ({ ...prev, tehsilCode: matchedTehsil.code }));
          }

          const tehsilCodeToUse = matchedTehsil ? matchedTehsil.code : "";
          if (tehsilCodeToUse) {
            const cities = await fetchCities(tehsilCodeToUse);
            const matchedCity = cities.find(c =>
              (c.description && c.description.toString() === (billingAddress.cityName || "").toString()) ||
              (c.code && c.code.toString() === (billingAddress.cityName || "").toString())
            );
            if (matchedCity) {
              setShippingAddress(prev => ({ ...prev, cityCode: matchedCity.code }));
            }
          }
        }
      }

      toast.success("Billing address copied to shipping address!");
    } catch (err) {
      console.error("Error copying billing to shipping:", err);
      toast.error("Failed to copy billing address fully");
    } finally {
      copyingShippingRef.current = false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, saving: true }));
    try {
      const payload = {
        invoiceId,
        shippingAddressId: shippingAddress.shippingAddressId || undefined,
        name: shippingAddress.name,
        address1: shippingAddress.address1,
        address2: shippingAddress.address2,
        address3: shippingAddress.address3,
        countryCode: shippingAddress.countryCode,
        regionCode: shippingAddress.regionCode,
        districtCode: shippingAddress.districtCode,
        tehsilCode: shippingAddress.tehsilCode,
        cityCode: shippingAddress.cityCode,
        pinCode: shippingAddress.pinCode
      };

      // reuse existing saveShippingAddress helper (assumed to accept this shape)
     const res=await axios.put(`${Base_URL}/Invoices/UpdateShippingAddress/id?id=${payload.invoiceId}`,payload);
     console.log(payload);
     if(res.status===200)
        toast.success(`${payload.invoiceId} Updated successfully`);
    } catch (err) {
      console.error("Error saving shipping address:", err);
      toast.error(err.message || "Failed to update shipping address");
    } finally {
      setLoading(prev => ({ ...prev, saving: false }));
    }
  };

  return (
    <div className="container-fluid py-4">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <div className="card shadow-sm border-0 rounded-4 p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="fw-bold text-dark mb-1">
              Update Shipping Address - Invoice #{invoiceId}
            </h4>
            <p className="text-muted mb-2">
              Load existing shipping address, modify and save.
            </p>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Billing Address */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 rounded-4 p-4 h-100">
            <div className="d-flex align-items-center mb-4">
              <div className="icon-box me-2">🏢</div>
              <h5 className="fw-bold mb-0 text-dark">Billing Address</h5>
            </div>

            {loading.customer ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading billing address...</p>
              </div>
            ) : (
              <div className="row g-3">
                {['name', 'address1', 'address2', 'address3', 'pinCode', 'countryCode', 'regionName', 'districtName', 'tehsilName', 'cityName'].map((field) => (
                  <div key={field} className="col-12">
                    <label className="form-label fw-semibold text-capitalize">
                      {field.replace(/([A-Z])/g, ' $1').trim()}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={billingAddress[field] || ''}
                      readOnly
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Shipping Address */}
        <div className="col-md-6">
          <div className="card shadow-sm border-0 rounded-4 p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <div className="icon-box me-2">🚚</div>
                <h5 className="fw-bold mb-0 text-dark">Shipping Address</h5>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={copyBillingToShipping}
              >
                📋 Copy Billing Address
              </button>
            </div>

            {loading.shippingLoad ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading shipping address...</span>
                </div>
                <p className="mt-2">Loading shipping address...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={shippingAddress.name}
                      onChange={handleShippingChange}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Address 1 *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address1"
                      value={shippingAddress.address1}
                      onChange={handleShippingChange}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Address 2</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address2"
                      value={shippingAddress.address2}
                      onChange={handleShippingChange}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Address 3</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address3"
                      value={shippingAddress.address3}
                      onChange={handleShippingChange}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Pin Code</label>
                    <input
                      type="text"
                      className="form-control"
                      name="pinCode"
                      value={shippingAddress.pinCode || ""}
                      onChange={handleShippingChange}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Country *</label>
                    <select
                      className="form-select"
                      name="countryCode"
                      value={shippingAddress.countryCode}
                      onChange={handleShippingChange}
                      required
                      disabled={loading.countries}
                    >
                      <option value="">Select Country</option>
                      {dropdownData.countries.map((country) => (
                        <option key={country.countryCode} value={country.countryCode}>
                          {country.countryCode} - {country.countryName}
                        </option>
                      ))}
                    </select>
                    {loading.countries && <small className="text-muted">Loading countries...</small>}
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Region *</label>
                    <select
                      className="form-select"
                      name="regionCode"
                      value={shippingAddress.regionCode}
                      onChange={handleShippingChange}
                      required
                      disabled={loading.regions || !shippingAddress.countryCode}
                    >
                      <option value="">Select Region</option>
                      {dropdownData.regions.map((region) => (
                        <option key={region.regionCode} value={region.regionCode}>
                          {region.regionCode} - {region.regionName}
                        </option>
                      ))}
                    </select>
                    {loading.regions && <small className="text-muted">Loading regions...</small>}
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">District *</label>
                    <select
                      className="form-select"
                      name="districtCode"
                      value={shippingAddress.districtCode}
                      onChange={handleShippingChange}
                      required
                      disabled={loading.districts || !shippingAddress.regionCode}
                    >
                      <option value="">Select District</option>
                      {dropdownData.districts.map((district) => (
                        <option key={district.code} value={district.code}>
                          {district.code} - {district.description}
                        </option>
                      ))}
                    </select>
                    {loading.districts && <small className="text-muted">Loading districts...</small>}
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Tehsil *</label>
                    <select
                      className="form-select"
                      name="tehsilCode"
                      value={shippingAddress.tehsilCode}
                      onChange={handleShippingChange}
                      required
                      disabled={loading.tehsils || !shippingAddress.districtCode}
                    >
                      <option value="">Select Tehsil</option>
                      {dropdownData.tehsils.map((tehsil) => (
                        <option key={tehsil.code} value={tehsil.code}>
                          {tehsil.code} - {tehsil.description}
                        </option>
                      ))}
                    </select>
                    {loading.tehsils && <small className="text-muted">Loading tehsils...</small>}
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">City *</label>
                    <select
                      className="form-select"
                      name="cityCode"
                      value={shippingAddress.cityCode}
                      onChange={handleShippingChange}
                      required
                      disabled={loading.cities || !shippingAddress.tehsilCode}
                    >
                      <option value="">Select City</option>
                      {dropdownData.cities.map((city) => (
                        <option key={city.code} value={city.code}>
                          {city.code} - {city.description}
                        </option>
                      ))}
                    </select>
                    {loading.cities && <small className="text-muted">Loading cities...</small>}
                  </div>

                  <div className="d-flex justify-content-center mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={loading.saving}
                    >
                      {loading.saving ? "Saving..." : "Save Shipping Address"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateShippingAddress;
